import { useMemo, type UIEvent } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import type { NotificationItem } from './types';
import { useNotifications } from './useNotifications';
import { groupByDate } from './dateGroups';
import { NotificationRow } from './NotificationRow';

interface Props {
  /** Open a notification's target in the pane (wired by TaskPane → chat.loadThread). */
  onOpen: (item: NotificationItem) => void;
}

/**
 * Full-pane notification panel (mirrors ChatHistorySidebar's overlay pattern):
 * date-grouped list, infinite scroll, mark-all-as-read. Visible only when
 * `panelOpen`.
 */
export function NotificationPanel({ onOpen }: Props): JSX.Element | null {
  const { items, panelOpen, closePanel, loading, hasMore, loadMore, markAllRead, unreadCount } =
    useNotifications();

  const groups = useMemo(() => groupByDate(items), [items]);

  if (!panelOpen) return null;

  const onScroll = (e: UIEvent<HTMLDivElement>): void => {
    const el = e.currentTarget;
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      void loadMore();
    }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-background">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-foreground" />
          <span className="text-sm font-semibold text-foreground">Notifications</span>
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => void markAllRead()}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Mark all as read"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              <span>Mark all read</span>
            </button>
          )}
          <button
            type="button"
            onClick={closePanel}
            aria-label="Close"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" onScroll={onScroll}>
        {items.length === 0 && !loading && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <Bell className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        )}

        {groups.map((group) => (
          <div key={group.bucket}>
            <div className="sticky top-0 bg-muted/60 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">
              {group.bucket}
            </div>
            <div className="divide-y divide-border">
              {group.items.map((item) => (
                <NotificationRow key={item.id} item={item} onOpen={onOpen} />
              ))}
            </div>
          </div>
        ))}

        {loading && (
          <p className="py-3 text-center text-xs text-muted-foreground">Loading…</p>
        )}
      </div>
    </div>
  );
}
